import { useEffect, useState } from "react";

import {
  getReportMentions,
  PaginatedReportList,
} from "@/api/endpoints/mentions";
import { SourceDisplay } from "@/components/DataDisplay";
import ListMention from "@/containers/ListMention";
import { useI18n } from "@/contexts/i18nContext";
import useSearchParams from "@/hooks/useSearchParams";
import { metricTitleMapping } from "@/mappings/metric_title_mapping";
import { cx, removePrefixKeys, round } from "@/utils";
import { truncate } from "@/utils/truncate";
import Modal, {
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@components/Modal";
import Pagination from "@components/Pagination";
import Sentiment from "@components/Sentiment";
import SpinnerAnimated from "@components/SpinnerAnimated";
import Table from "@components/Table";
import Typography from "@components/Typography";
import { i18n } from "@lingui/core";

import Cover from "../Episode/partials/Cover";
import InfoLink from "../InfoLink";
import InsightHeader from "./partials/InsightHeader";
import {
  DEFAULT_DATES_API_FRIENDLY,
  MentionModalUrlParameters,
  MENTIONS_MODAL_OPEN_PARAM,
  MODAL_FILTER_PREFIX,
} from "./settings";
import { clearPrefixedKeys } from "./utils";

import styles from "./MentionsModalURL.module.css";

type MentionsModalURLProps = { className?: string; title?: string };

/**
 * MentionsModalURL
 *
 * Displays a set of mentions or channels based on what is present in the URL.
 * It could technically display both channels and mentions, but that is not
 * advisable. Please don't pass both in the response.
 */
const MentionsModalURL: React.FunctionComponent<MentionsModalURLProps> = ({
  className,
}) => {
  const [params, setParams] = useSearchParams<MentionModalUrlParameters>();
  const openModalMetric = params[MENTIONS_MODAL_OPEN_PARAM];
  const [data, setData] = useState<PaginatedReportList | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { abbreviateNumber, formatDateRange } = useI18n();
  const [page, setPage] = useState<number>(1);

  const handleClose = () => {
    setData(null);
    setLoading(false);
    setPage(1);
    setParams(
      {
        ...clearPrefixedKeys(params),
        [MENTIONS_MODAL_OPEN_PARAM]: undefined,
      },
      true
    );
  };

  useEffect(() => {
    if (params[MENTIONS_MODAL_OPEN_PARAM] == null) {
      return;
    }
    const fetchMentions = async () => {
      setLoading(true);
      try {
        const { data, ok } = await getReportMentions({
          ...DEFAULT_DATES_API_FRIENDLY,
          ...removePrefixKeys(MODAL_FILTER_PREFIX, params),
          page,
        });
        if (data && ok) {
          setData(data);
        }
      } catch {
        // do nothing
      } finally {
        setLoading(false);
      }
    };
    fetchMentions();
  }, [params, page]);

  return (
    <Modal
      className={cx(styles.root, className)}
      show={Boolean(openModalMetric)}
      onHide={handleClose}
    >
      <ModalHeader className={cx(styles.header, className)}>
        <ModalDescription>
          {formatDateRange(params.m_start_date, params.m_end_date, "PPP")}
        </ModalDescription>
        <ModalTitle className={styles.title} data-testid="mentions-modal-title">
          {openModalMetric
            ? i18n._(metricTitleMapping[openModalMetric] || "")
            : ""}
        </ModalTitle>
        <InsightHeader />
      </ModalHeader>

      <ModalContent className={styles.content}>
        {loading ? (
          <div className={styles.spinner}>
            <SpinnerAnimated />
          </div>
        ) : (
          <>
            {data?.mentions?.map((mention, index) => (
              <ListMention
                key={index}
                channel_name={mention.channel_title}
                title={mention.episode_title}
                source={mention.channel_type}
                external_source={mention.external_source}
                published_date={mention.episode_published_date}
                text={mention.text}
              >
                {mention.sentiment && (
                  <Sentiment
                    value={mention.sentiment}
                    className={styles.icon}
                  />
                )}
              </ListMention>
            ))}
            {data?.channels ? (
              <Table className={styles.channelTable}>
                <thead>
                  <tr>
                    <th>{L.REPORTS_WIDGET_REACH_TABLE_SOURCE}</th>
                    <th align="right">
                      {L.REPORTS_WIDGET_REACH_TABLE_REACH}
                      <InfoLink
                        content={L.MENTIONS_REACH_HOW_CALCULATED}
                        href={L.HELP_ARTICLE_REACH}
                      />
                    </th>
                    <th>{L.REPORTS_WIDGET_REACH_TABLE_MEDIA}</th>
                    <th />
                  </tr>
                </thead>
                <tbody data-testid="mentions-modal-channels-list">
                  {data.channels.map((channel, index) => (
                    <tr key={index}>
                      <td className={styles.coverWrapper}>
                        <Cover
                          className={styles.cover}
                          dumb
                          episode={{
                            source: channel.channel_type,
                            cover: channel.cover,
                          }}
                        />
                        <Typography margin="none">
                          <strong>
                            {channel.name
                              ? truncate(channel.name, 50)
                              : L.REPORTS_WIDGET_UNKNOWN_SOURCE_NAME}
                          </strong>
                          <br />
                          {L.REPORTS_WIDGET_REACH_TABLE_MENTIONS}:{" "}
                          {channel.mentions_count}
                        </Typography>
                      </td>
                      <td>
                        <Typography margin="none">
                          {abbreviateNumber(round(channel.reach))}
                        </Typography>
                      </td>
                      <td>
                        <Typography margin="none">
                          <SourceDisplay id={channel.channel_type} />
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <Pagination
          max={data?.meta.total_num_pages || 0}
          current={data?.meta.page_number}
          previous={() => setPage((p) => p - 1)}
          next={() => setPage((p) => p + 1)}
        />
      </ModalFooter>
    </Modal>
  );
};

export default MentionsModalURL;
